import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import api from "../../../services/api";
import EmployeeTable from "./AdminEmployeeTable";
import EmployeeFormModal from "./AdminEmployeeFormModal";
import useEmployeeFormData from "../../../hooks/useEmployeeFormData";
import ConfirmDeleteDialog from "../../../components/ConfirmDeleteDialog";
import { useSnackbarAlert } from "../../../hooks/useSnackbarAlert";

const EmployeesPage = () => {
  const [employees, setEmployees] = useState([]);
  const [storeLocations, setStoreLocations] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const snackbar = useSnackbarAlert();

  const {
    formData,
    handleChange,
    handlePermissionChange,
    handleSubmit,
    resetForm,
    populateForm,
    isEditMode,
    isSubmitting,
    errors,
  } = useEmployeeFormData();

  const fetchEmployees = async () => {
    try {
      const res = await api.get("/admin/employees");
      setEmployees(res.data);
    } catch (err) {
      console.error("Failed to fetch employees:", err);
    }
  };

  const fetchStoreLocations = async () => {
    try {
      const res = await api.get("/admin/stores");
      setStoreLocations(res.data);
    } catch (err) {
      console.error("Failed to fetch store locations:", err);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchStoreLocations();
  }, []);

  const handleAddNew = () => {
    resetForm();
    setOpenModal(true);
  };

  const handleEdit = (employee) => {
    populateForm(employee);
    setOpenModal(true);
  };

  const triggerDelete = (id) => {
    setDeleteTargetId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    setIsDeleting(true);
    try {
      await api.delete(`/admin/employees/${deleteTargetId}`);
      snackbar.success("Employee deleted!");
      fetchEmployees();
    } catch (err) {
      snackbar.error("Failed to delete employee!");
      console.error("Failed to delete employee:", err);
    } finally {
      setIsDeleting(false);
      setConfirmOpen(false);
      setDeleteTargetId(null);
    }
  };

  const handleSave = async () => {
    const result = await handleSubmit();
    if (result.success) {
      fetchEmployees();
      setOpenModal(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          Employees
        </Typography>
        <Tooltip title="Add New Employee">
          <IconButton color="primary" onClick={handleAddNew}>
            <AddCircleIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <EmployeeTable
        employees={employees}
        onEdit={handleEdit}
        onDelete={triggerDelete}
      />

      <EmployeeFormModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSave}
        formData={formData}
        storeLocations={storeLocations}
        handleChange={handleChange}
        handlePermissionChange={handlePermissionChange}
        isEditMode={isEditMode}
        isSubmitting={isSubmitting}
        errors={errors}
      />

      <ConfirmDeleteDialog
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
      />
    </Box>
  );
};

export default EmployeesPage;
